import { computed, Ref, ref, watch } from 'vue';
import { i18n } from '@/i18n';

/**
 * 曜日を表すインデックス。0が日曜日で6が土曜日。
 */
export type DayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * カレンダー1日の表示情報。
 */
export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

/**
 * カレンダー1週分の表示情報。
 */
export interface CalendarWeek {
  days: CalendarDay[];
  weekNumber: number;
  monthWeekIndex: number;
}

/**
 * カレンダーロジックの設定。
 */
export interface UseCalendarOptions {
  selectedDate: Ref<Date | null>;
  firstDayOfWeek?: DayIndex;
}

/**
 * 月移動や日付選択などカレンダー表示用のロジックを提供する。
 * @param options - 選択状態や週の開始曜日設定
 */
export const useCalendar = (options: UseCalendarOptions) => {
  const firstDayOfWeek: DayIndex = options.firstDayOfWeek ?? 0;
  const today = normalizeDate(new Date());
  const initialDisplayDate = normalizeDate(options.selectedDate.value) ?? today ?? new Date();
  const displayDate = ref<Date>(initialDisplayDate);

  const normalizedSelectedDate = computed(() => normalizeDate(options.selectedDate.value));
  const locale = computed(() => i18n.locale);

  watch(
    options.selectedDate,
    (value) => {
      if (value) {
        displayDate.value = normalizeDate(value) ?? displayDate.value;
      }
    },
    { immediate: true }
  );

  const displayYearMonth = computed(() => {
    return {
      year: displayDate.value.getFullYear(),
      month: displayDate.value.getMonth() + 1,
      eraLabel: formatJapaneseEra(displayDate.value),
    };
  });

  const headerLabel = computed(() => {
    const { year, month, eraLabel } = displayYearMonth.value;
    if (locale.value === 'ja') {
      return `${year}年(${eraLabel}) ${month}月`;
    }
    const paddedMonth = `${month}`.padStart(2, '0');
    return `${year}/${paddedMonth}`;
  });

  const weeks = computed<CalendarWeek[]>(() => {
    return buildWeeks(displayDate.value, normalizedSelectedDate.value, today, firstDayOfWeek);
  });

  /**
   * 月を移動する。
   * @param diff - 移動する月数
   */
  const moveMonth = (diff: number) => {
    const next = new Date(displayDate.value);
    next.setDate(1);
    next.setMonth(displayDate.value.getMonth() + diff);
    displayDate.value = normalizeDate(next) ?? displayDate.value;
  };

  /**
   * 日付を選択する。
   * @param target - 選択する日付
   */
  const selectDate = (target: Date) => {
    const normalized = normalizeDate(target);
    if (!normalized) return;
    const base = options.selectedDate.value ? new Date(options.selectedDate.value) : new Date(normalized);
    base.setFullYear(normalized.getFullYear(), normalized.getMonth(), normalized.getDate());
    displayDate.value = new Date(normalized);
    options.selectedDate.value = base;
  };

  /**
   * 選択状態を解除する。
   */
  const clearSelection = () => {
    options.selectedDate.value = null;
  };

  /**
   * 今日に移動し、今日を選択する。
   */
  const goToday = () => {
    const now = normalizeDate(new Date());
    displayDate.value = now ?? displayDate.value;
    options.selectedDate.value = now ? new Date(now) : null;
  };

  /**
   * 年月を直接指定して表示を移動する。
   * @param year - 年
   * @param month - 月(1-12)
   */
  const setMonth = (year: number, month: number) => {
    const next = new Date(year, month - 1, 1);
    displayDate.value = normalizeDate(next) ?? displayDate.value;
  };

  return { weeks, headerLabel, displayYearMonth, moveMonth, selectDate, clearSelection, goToday, setMonth };
};

/**
 * 日付オブジェクトを00:00:00に正規化する。
 * @param value - 正規化対象の日付
 */
const normalizeDate = (value?: Date | null): Date | null => {
  if (!value) return null;
  const normalized = new Date(value);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * 2つの日付が同一日かを判定する。
 * @param a - 比較する日付A
 * @param b - 比較する日付B
 */
const isSameDay = (a: Date | null, b: Date | null): boolean => {
  return !!a && !!b && a.getTime() === b.getTime();
};

/**
 * 6週分のカレンダーデータを組み立てる。
 * @param baseDate - 表示基準となる年月日
 * @param selectedDate - 選択されている日付
 * @param today - 今日の日付
 * @param firstDayOfWeek - 週の開始曜日
 */
const buildWeeks = (baseDate: Date, selectedDate: Date | null, today: Date | null, firstDayOfWeek: DayIndex): CalendarWeek[] => {
  const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const startOffset = (start.getDay() - firstDayOfWeek + 7) % 7;
  const cursor = new Date(start);
  cursor.setDate(cursor.getDate() - startOffset);

  const weeks: CalendarWeek[] = [];
  let monthWeekIndex = 0;

  for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
    const days: CalendarDay[] = [];
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const cellDate = new Date(cursor);
      const isCurrentMonth = cellDate.getMonth() === baseDate.getMonth();
      days.push({
        date: cellDate,
        isCurrentMonth,
        isSelected: isSameDay(cellDate, selectedDate),
        isToday: isSameDay(cellDate, today),
        isWeekend: cellDate.getDay() === 0 || cellDate.getDay() === 6,
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    if (days.some((day) => day.isCurrentMonth)) {
      monthWeekIndex += 1;
    }

    weeks.push({
      days,
      weekNumber: calculateIsoWeekNumber(days[0].date),
      monthWeekIndex: monthWeekIndex,
    });
  }

  return weeks;
};

/**
 * ISO週番号を算出する。
 * @param date - 週番号を求める対象日
 */
const calculateIsoWeekNumber = (date: Date): number => {
  const tmpDate = new Date(date);
  tmpDate.setHours(0, 0, 0, 0);
  tmpDate.setDate(tmpDate.getDate() + 4 - (tmpDate.getDay() || 7));
  const yearStart = new Date(tmpDate.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((tmpDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return weekNumber;
};

/**
 * 元号付きの日付表記に変換する。
 * @param date - 変換する日付
 */
const formatJapaneseEra = (date: Date): string => {
  const eras = [
    { name: '令和', start: new Date(2019, 4, 1) },
    { name: '平成', start: new Date(1989, 0, 8) },
    { name: '昭和', start: new Date(1926, 11, 25) },
  ];

  for (const era of eras) {
    if (date >= era.start) {
      const eraYear = date.getFullYear() - era.start.getFullYear() + 1;
      return `${era.name}${eraYear}年`;
    }
  }

  return `${date.getFullYear()}年`;
};
